import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  Clock,
  CheckCircle2,
  Circle,
  TrendingUp,
  TrendingDown,
  Minus,
  CalendarClock,
  Activity,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ArrowRight,
  Target,
  BarChart3,
  Timer,
  List,
  Download,
  Filter,
  Layers,
  GitCompareArrows,
  AlertTriangle,
  Zap,
} from 'lucide-react';

// ─── Constants ───────────────────────────────────────────────────────

const PRIORITY_COLORS = { high: 'border-l-red-500', medium: 'border-l-yellow-500', low: 'border-l-blue-400' };
const PRIORITY_BADGE = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};
const STATUS_STYLES = {
  completed: { icon: CheckCircle2, cls: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/10' },
  ongoing: { icon: Clock, cls: 'text-yellow-500', bg: 'bg-yellow-50 dark:bg-yellow-900/10' },
  draft: { icon: Circle, cls: 'text-muted-foreground', bg: '' },
  pending: { icon: Circle, cls: 'text-muted-foreground', bg: '' },
};
const GANTT_COLORS = {
  completed: { bar: '#22c55e', text: '#166534' },
  ongoing: { bar: '#eab308', text: '#854d0e' },
  draft: { bar: '#94a3b8', text: '#475569' },
  pending: { bar: '#94a3b8', text: '#475569' },
};

// ─── Helpers ─────────────────────────────────────────────────────────

function parseInjectTime(timeStr) {
  if (!timeStr) return null;
  const iso = new Date(timeStr);
  if (!isNaN(iso.getTime()) && timeStr.includes('-')) return iso;
  const parts = timeStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{2}):(\d{2})/);
  if (parts) {
    const a = parseInt(parts[1]), b = parseInt(parts[2]);
    // Auto-detect: if second number > 12, format is MM/DD/YYYY; otherwise DD/MM/YYYY
    const isMMDD = b > 12 || (a <= 12 && b <= 12);
    const month = isMMDD ? a - 1 : b - 1;
    const day = isMMDD ? b : a;
    return new Date(parseInt(parts[3]), month, day, parseInt(parts[4]), parseInt(parts[5]));
  }
  return null;
}

function formatTimeForTz(date, timezone) {
  if (!date) return '\u2014';
  try {
    return date.toLocaleString('en-GB', { timeZone: timezone, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return date.toLocaleString(); }
}

/** Format minutes offset as +XhYYm or +Xd Yh */
function formatOffset(minutes) {
  if (minutes === null || minutes === undefined) return '\u2014';
  const sign = minutes >= 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  if (abs < 60) return `${sign}${abs}m`;
  if (abs < 1440) return `${sign}${Math.floor(abs / 60)}h${String(abs % 60).padStart(2, '0')}m`;
  const days = Math.floor(abs / 1440);
  const hrs = Math.floor((abs % 1440) / 60);
  return `${sign}${days}d ${hrs}h`;
}


function formatTimeShort(date, timezone) {
  if (!date) return '\u2014';
  try { return date.toLocaleString('en-GB', { timeZone: timezone, hour: '2-digit', minute: '2-digit' }); }
  catch { return date.toLocaleTimeString(); }
}

function getPlannedTime(inject) {
  const m = inject?.metadata || {};
  return m['Time of Inject (UTC)'] || m['Inject Time'] || m['Inject Time (UTC)'] || m.time_of_inject_utc || m.inject_time || m.time_of_inject || inject?.inject_time || null;
}

function getVariance(planned, actual) {
  if (!planned || !actual) return null;
  return Math.round((actual - planned) / 60000);
}

function getPhase(inject) {
  const m = inject?.metadata || {};
  return m.Phase || m.phase || m['Exercise Phase'] || m.exercise_phase || m['Inject Category'] || m.inject_category || null;
}

function formatElapsed(ms) {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
}

function getDeviationColor(minutes) {
  if (minutes === null || minutes === undefined) return 'bg-muted/30';
  const abs = Math.abs(minutes);
  if (abs <= 2) return 'bg-green-500/20 dark:bg-green-500/10';
  if (minutes > 0 && abs > 30) return 'bg-red-500/20 dark:bg-red-500/10';
  if (minutes > 0) return 'bg-orange-500/20 dark:bg-orange-500/10';
  return 'bg-blue-500/20 dark:bg-blue-500/10';
}

// ─── Sub-components ──────────────────────────────────────────────────

function VarianceBadge({ minutes }) {
  if (minutes === null || minutes === undefined) return null;
  const abs = Math.abs(minutes);
  if (abs <= 2) return (
    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
      <Minus className="w-3 h-3" /> On time
    </span>
  );
  const label = formatOffset(minutes) + (minutes > 0 ? ' late' : ' early');
  if (minutes > 0) {
    const cls = abs > 30 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
    return <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}><TrendingDown className="w-3 h-3" /> {label}</span>;
  }
  return <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"><TrendingUp className="w-3 h-3" /> {label}</span>;
}

function ElapsedTimer({ exercise }) {
  const [elapsed, setElapsed] = useState(0);
  const startTime = exercise?.start_date ? new Date(exercise.start_date) : null;
  const isActive = exercise?.status === 'active';

  useEffect(() => {
    if (!startTime || !isActive) return;
    const tick = () => setElapsed(Date.now() - startTime.getTime());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startTime, isActive]);

  if (!startTime || !isActive) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 text-primary" data-testid="elapsed-timer">
      <Timer className="w-4 h-4 animate-pulse" />
      <span className="font-mono text-sm font-bold">{formatElapsed(elapsed)}</span>
      <span className="text-[10px] uppercase opacity-70">elapsed</span>
    </div>
  );
}

// ─── InjectTimelineCard (read-only) ──────────────────────────────────

function InjectTimelineCard({ inject, timezone, showDeviation }) {
  const [expanded, setExpanded] = useState(false);
  const meta = inject?.metadata || {};

  const plannedRaw = getPlannedTime(inject);
  const plannedDate = parseInjectTime(plannedRaw);
  const actualDate = inject.triggered_at ? new Date(inject.triggered_at) : null;
  const variance = getVariance(plannedDate, actualDate);

  const statusInfo = STATUS_STYLES[inject.status] || STATUS_STYLES.draft;
  const StatusIcon = statusInfo.icon;
  const borderColor = PRIORITY_COLORS[inject.priority] || 'border-l-gray-300';
  const badgeCls = PRIORITY_BADGE[inject.priority] || '';
  const deviationBg = showDeviation && actualDate ? getDeviationColor(variance) : '';

  const recipient = meta.Recipient || meta.recipient || meta['Inject Category'] || meta.inject_category || '';
  const method = meta.Method || meta.method || '';
  const keyOutcomes = meta['Key Outcomes'] || meta.key_outcomes || '';
  const suppInfo = meta['Supplemental Information'] || meta.supplemental_information || '';

  return (
    <div className={`rounded-lg border border-l-[3px] ${borderColor} ${statusInfo.bg} ${deviationBg} mb-2 transition-all`} data-testid="timeline-inject-card">
      <div className="p-3">
        <div className="flex items-start gap-2">
          <div className="shrink-0 mt-0.5">
            <StatusIcon className={`w-4 h-4 ${statusInfo.cls}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">#{inject.inject_number || '\u2014'}</span>
              <span className="font-medium text-sm truncate max-w-[200px]">{inject.title?.split('\n')[0] || 'Unnamed'}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badgeCls}`}>{inject.priority}</span>
              <Badge variant="outline" className={`text-[10px] capitalize ${inject.status === 'completed' ? 'border-green-400 text-green-600' : inject.status === 'ongoing' ? 'border-yellow-400 text-yellow-600' : 'border-border text-muted-foreground'}`}>{inject.status}</Badge>
            </div>
            {recipient && <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1"><Target className="w-3 h-3" /> {recipient}</p>}
          </div>
          <button className="text-muted-foreground hover:text-foreground shrink-0 p-1" onClick={() => setExpanded(e => !e)}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
        <div className="mt-2 ml-6 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[11px] text-muted-foreground">Planned:</span>
            <span className="text-xs font-mono font-semibold">{plannedDate ? formatTimeForTz(plannedDate, timezone) : plannedRaw || '\u2014'}</span>
          </div>
          <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
          <div className="flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[11px] text-muted-foreground">Actual:</span>
            <span className={`text-xs font-mono font-semibold ${actualDate ? '' : 'text-muted-foreground italic'}`}>{actualDate ? formatTimeForTz(actualDate, timezone) : 'Not triggered'}</span>
          </div>
          {variance !== null && <VarianceBadge minutes={variance} />}
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 ml-6 space-y-2 border-t pt-2">
          {inject.content && <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-0.5">Inject Information</p><p className="text-xs whitespace-pre-wrap">{inject.content}</p></div>}
          {method && <div className="flex gap-4 text-xs"><span className="text-muted-foreground">Method:</span><span>{method}</span></div>}
          {keyOutcomes && <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-0.5">Key Outcomes</p><p className="text-xs whitespace-pre-wrap">{keyOutcomes}</p></div>}
          {suppInfo && <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-0.5">Supplemental Info</p><p className="text-xs whitespace-pre-wrap">{suppInfo}</p></div>}
        </div>
      )}
    </div>
  );
}

// ─── Gantt Chart View ────────────────────────────────────────────────

function GanttChart({ injects, timezone, exercise }) {
  const ganttRef = useRef(null);
  const nowLineRef = useRef(null);

  const { minTime, maxTime, rows, nowPos } = useMemo(() => {
    const withTime = injects.map(inj => ({
      ...inj,
      plannedDate: parseInjectTime(getPlannedTime(inj)),
      actualDate: inj.triggered_at ? new Date(inj.triggered_at) : null,
    })).filter(inj => inj.plannedDate);

    if (withTime.length === 0) return { minTime: null, maxTime: null, rows: [], nowPos: null };

    const times = withTime.map(i => i.plannedDate.getTime());
    if (withTime.some(i => i.actualDate)) times.push(...withTime.filter(i => i.actualDate).map(i => i.actualDate.getTime()));

    let min = Math.min(...times);
    let max = Math.max(...times);
    const range = max - min;
    const pad = Math.max(range * 0.1, 600000); // at least 10 minutes padding
    min -= pad;
    max += pad;

    const now = Date.now();
    const nPos = now >= min && now <= max ? ((now - min) / (max - min)) * 100 : null;

    const rows = withTime.map(inj => {
      const pPos = ((inj.plannedDate.getTime() - min) / (max - min)) * 100;
      const aPos = inj.actualDate ? ((inj.actualDate.getTime() - min) / (max - min)) * 100 : null;
      const variance = getVariance(inj.plannedDate, inj.actualDate);
      return { ...inj, pPos, aPos, variance };
    });

    return { minTime: new Date(min), maxTime: new Date(max), rows, nowPos: nPos };
  }, [injects]);

  // Auto-scroll to NOW line
  useEffect(() => {
    if (nowLineRef.current && ganttRef.current) {
      const container = ganttRef.current;
      const line = nowLineRef.current;
      const containerRect = container.getBoundingClientRect();
      const lineRect = line.getBoundingClientRect();
      const offset = lineRect.left - containerRect.left - containerRect.width / 2;
      container.scrollLeft += offset;
    }
  }, [nowPos]);

  if (!minTime || rows.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-lg">
        <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <p className="text-sm font-medium">No injects with planned times found</p>
        <p className="text-xs mt-1">Add inject times (via metadata) to see the Gantt view</p>
      </div>
    );
  }

  // Generate time axis markers
  const totalMs = maxTime.getTime() - minTime.getTime();
  const markerCount = Math.min(12, Math.max(4, Math.floor(totalMs / 1800000))); // marker every ~30min
  const markers = [];
  for (let i = 0; i <= markerCount; i++) {
    const t = new Date(minTime.getTime() + (totalMs * i / markerCount));
    markers.push({ time: t, pos: (i / markerCount) * 100 });
  }

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="gantt-chart">
      {/* Time axis header */}
      <div className="bg-muted/50 border-b px-2 py-1.5 overflow-x-auto" ref={ganttRef}>
        <div className="relative h-6 min-w-[800px]">
          {markers.map((m, i) => (
            <div key={i} className="absolute top-0 text-[9px] font-mono text-muted-foreground" style={{ left: `${m.pos}%`, transform: 'translateX(-50%)' }}>
              {formatTimeShort(m.time, timezone)}
            </div>
          ))}
        </div>

        {/* Gantt rows */}
        <div className="space-y-1 min-w-[800px] pb-2">
          {rows.map((row) => {
            const colors = GANTT_COLORS[row.status] || GANTT_COLORS.draft;
            return (
              <div key={row.inject_id} className="relative h-8 group">
                {/* Grid lines */}
                {markers.map((m, i) => (
                  <div key={i} className="absolute top-0 bottom-0 w-px bg-border/40" style={{ left: `${m.pos}%` }} />
                ))}

                {/* NOW line */}
                {nowPos !== null && (
                  <div ref={nowLineRef} className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20" style={{ left: `${nowPos}%` }} />
                )}

                {/* Planned position marker (ghost) */}
                <div
                  className="absolute top-1 h-6 rounded opacity-30 border border-dashed border-border"
                  style={{ left: `${Math.max(0, row.pPos - 1)}%`, width: '2%', backgroundColor: colors.bar }}
                  title={`Planned: ${formatTimeForTz(row.plannedDate, timezone)}`}
                />

                {/* Actual position marker (solid) */}
                {row.aPos !== null && (
                  <div
                    className="absolute top-1 h-6 rounded shadow-sm z-10"
                    style={{ left: `${Math.max(0, row.aPos - 1)}%`, width: '2%', backgroundColor: colors.bar }}
                    title={`Actual: ${formatTimeForTz(row.actualDate, timezone)}`}
                  />
                )}

                {/* Deviation connector line */}
                {row.aPos !== null && row.pPos !== row.aPos && (
                  <div
                    className="absolute top-[16px] h-px z-5"
                    style={{
                      left: `${Math.min(row.pPos, row.aPos)}%`,
                      width: `${Math.abs(row.aPos - row.pPos)}%`,
                      backgroundColor: row.variance > 5 ? '#ef4444' : row.variance < -5 ? '#3b82f6' : '#22c55e',
                    }}
                  />
                )}

                {/* Label */}
                <div className="absolute top-1 left-0 h-6 flex items-center pointer-events-none z-30 pl-1">
                  <span className="text-[9px] font-mono bg-card/90 px-1 py-0.5 rounded shadow-sm border truncate max-w-[150px]">
                    #{row.inject_number || '?'} {row.title?.split('\n')[0]?.slice(0, 20) || ''}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-3 py-2 text-[10px] text-muted-foreground bg-muted/30 border-t">
        <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded bg-muted-foreground opacity-30 border border-dashed border-border" /> Planned</span>
        <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded bg-green-500" /> Actual (on time)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded bg-yellow-500" /> In progress</span>
        {nowPos !== null && <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-red-500" /> Now</span>}
      </div>
    </div>
  );
}

// ─── Phase Group View ────────────────────────────────────────────────

function PhaseGroupView({ injects, timezone, showDeviation }) {
  const [collapsedPhases, setCollapsedPhases] = useState(new Set());

  const grouped = useMemo(() => {
    const groups = {};
    injects.forEach(inj => {
      const phase = getPhase(inj) || 'Ungrouped';
      if (!groups[phase]) groups[phase] = [];
      groups[phase].push(inj);
    });
    // Sort each group by planned time
    Object.values(groups).forEach(arr => arr.sort((a, b) => {
      const aT = parseInjectTime(getPlannedTime(a));
      const bT = parseInjectTime(getPlannedTime(b));
      if (aT && bT) return aT - bT;
      return (parseInt(a.inject_number) || 0) - (parseInt(b.inject_number) || 0);
    }));
    return groups;
  }, [injects]);

  const phaseNames = Object.keys(grouped);

  const togglePhase = (p) => {
    setCollapsedPhases(prev => {
      const next = new Set(prev);
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });
  };

  return (
    <div className="space-y-3" data-testid="phase-group-view">
      {phaseNames.map(phase => {
        const items = grouped[phase];
        const isCollapsed = collapsedPhases.has(phase);
        const completed = items.filter(i => i.status === 'completed').length;
        const total = items.length;
        const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

        return (
          <div key={phase} className="border rounded-lg overflow-hidden">
            <button
              onClick={() => togglePhase(phase)}
              className="w-full flex items-center justify-between p-3 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
              data-testid={`phase-toggle-${phase}`}
            >
              <div className="flex items-center gap-2">
                <ChevronRight className={`w-4 h-4 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
                <Layers className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm">{phase}</span>
                <Badge variant="secondary" className="text-[10px]">{total} injects</Badge>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[10px] text-muted-foreground font-mono w-10 text-right">{pct}%</span>
              </div>
            </button>
            {!isCollapsed && (
              <div className="p-2 space-y-0">
                {items.map((inj, idx) => (
                  <InjectTimelineCard key={inj.inject_id || idx} inject={inj} timezone={timezone} showDeviation={showDeviation} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Activity Feed with Filters ──────────────────────────────────────

function ActivityFeed({ events, timezone }) {
  const [filter, setFilter] = useState('all');

  const typeColors = {
    inject_triggered: 'border-l-orange-400',
    inject_acknowledged: 'border-l-green-400',
    inject_response: 'border-l-purple-400',
    inject_completed: 'border-l-green-500',
    message: 'border-l-blue-400',
    phone_call: 'border-l-sky-400',
    manual_log: 'border-l-gray-400',
    media_portal: 'border-l-red-400',
    mp_post_published: 'border-l-red-400',
    media_portal_publish: 'border-l-red-400',
    exercise_start: 'border-l-green-600',
    exercise_end: 'border-l-red-600',
    exercise_pause: 'border-l-yellow-600',
  };

  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'inject', label: 'Injects' },
    { value: 'exercise', label: 'Exercise' },
    { value: 'media', label: 'Media Portal' },
    { value: 'message', label: 'Messages' },
    { value: 'other', label: 'Other' },
  ];

  const filteredEvents = useMemo(() => {
    const sorted = [...events].sort((a, b) => new Date(b.utc_time || b.created_at) - new Date(a.utc_time || a.created_at));
    if (filter === 'all') return sorted;
    return sorted.filter(e => {
      const type = e.log_type || e.activity_type || e.event_type || '';
      if (filter === 'inject') return type.includes('inject');
      if (filter === 'exercise') return type.includes('exercise');
      if (filter === 'media') return type.includes('media') || type.includes('mp_');
      if (filter === 'message') return type.includes('message');
      return !type.includes('inject') && !type.includes('exercise') && !type.includes('media') && !type.includes('mp_') && !type.includes('message');
    });
  }, [events, filter]);

  return (
    <div className="mt-4 pt-4 border-t" data-testid="activity-feed">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" /> Live Activity Feed
          <Badge variant="secondary" className="text-[10px]">{filteredEvents.length}</Badge>
        </h3>
        <div className="flex items-center gap-1">
          <Filter className="w-3 h-3 text-muted-foreground" />
          {filterOptions.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${filter === f.value ? 'bg-primary text-primary-foreground font-medium' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
              data-testid={`feed-filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <ScrollArea className="h-[300px]">
        <div className="pr-2">
          {filteredEvents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-xs">No events match filter</p>
            </div>
          ) : filteredEvents.map((item, idx) => {
            const type = item.log_type || item.activity_type || item.event_type || 'event';
            const border = typeColors[type] || 'border-l-gray-300';
            const isMP = type.includes('media') || type.includes('mp_');
            const label = isMP ? 'Media Portal' : type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

            return (
              <div key={item.log_id || item.event_id || idx} className={`rounded-lg border border-l-[3px] ${border} bg-card p-3 mb-2`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {isMP && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-bold uppercase">MP</span>}
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
                      {item.from_role && <span className="text-xs text-muted-foreground">{item.from_role}{item.to_role ? ` \u2192 ${item.to_role}` : ''}</span>}
                      {item.mp_platform && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">{item.mp_platform}</span>}
                    </div>
                    <p className="text-xs mt-0.5 break-words">{item.message || item.description}</p>
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">{formatTimeForTz(new Date(item.utc_time || item.created_at), timezone)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Export Timeline ─────────────────────────────────────────────────

function ExportTimelineButton({ injects, exercise, timezone }) {
  const handleExport = () => {
    const rows = injects.map(inj => {
      const planned = parseInjectTime(getPlannedTime(inj));
      const actual = inj.triggered_at ? new Date(inj.triggered_at) : null;
      const variance = getVariance(planned, actual);
      const phase = getPhase(inj) || '';
      return [
        inj.inject_number || '',
        (inj.title || '').replace(/[\n,]/g, ' ').slice(0, 80),
        phase,
        inj.priority || '',
        inj.status || '',
        planned ? formatTimeForTz(planned, timezone) : '',
        actual ? formatTimeForTz(actual, timezone) : 'Not triggered',
        variance !== null ? (variance > 0 ? `+${variance}m late` : variance < 0 ? `${variance}m early` : 'On time') : '',
      ].join(',');
    });

    const csv = ['#,Title,Phase,Priority,Status,Planned,Actual,Variance', ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timeline_${exercise?.name?.replace(/\s+/g, '_') || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Timeline exported as CSV');
  };

  return (
    <Button size="sm" variant="outline" onClick={handleExport} className="gap-1.5" data-testid="export-timeline-btn">
      <Download className="w-3.5 h-3.5" />
      Export
    </Button>
  );
}

// ─── Compare View ────────────────────────────────────────────────────

function CompareView({ injects, timezone, exercise }) {
  const [sortBy, setSortBy] = useState('planned'); // planned | deviation | number

  const rows = useMemo(() => {
    // Find the first planned time (scenario start reference)
    const allPlanned = injects
      .map(inj => parseInjectTime(getPlannedTime(inj)))
      .filter(Boolean)
      .sort((a, b) => a - b);
    const scenarioStart = allPlanned.length > 0 ? allPlanned[0] : null;
    const exerciseStart = exercise?.start_date ? new Date(exercise.start_date) : null;

    const mapped = injects.map(inj => {
      const plannedDate = parseInjectTime(getPlannedTime(inj));
      const actualDate = inj.triggered_at ? new Date(inj.triggered_at) : null;
      const phase = getPhase(inj) || 'Ungrouped';

      // Relative offsets (in minutes)
      const plannedOffset = plannedDate && scenarioStart
        ? Math.round((plannedDate - scenarioStart) / 60000) : null;
      const actualOffset = actualDate && exerciseStart
        ? Math.round((actualDate - exerciseStart) / 60000) : null;

      // Deviation: actual offset vs planned offset (minutes)
      const variance = (plannedOffset !== null && actualOffset !== null)
        ? actualOffset - plannedOffset : null;

      return { ...inj, plannedDate, actualDate, variance, phase, plannedOffset, actualOffset };
    });

    if (sortBy === 'deviation') {
      return [...mapped].sort((a, b) => {
        if (a.variance === null && b.variance === null) return 0;
        if (a.variance === null) return 1;
        if (b.variance === null) return -1;
        return Math.abs(b.variance) - Math.abs(a.variance);
      });
    }
    if (sortBy === 'number') {
      return [...mapped].sort((a, b) => (parseInt(a.inject_number) || 0) - (parseInt(b.inject_number) || 0));
    }
    return [...mapped].sort((a, b) => {
      if (a.plannedDate && b.plannedDate) return a.plannedDate - b.plannedDate;
      if (a.plannedDate) return -1;
      if (b.plannedDate) return 1;
      return 0;
    });
  }, [injects, sortBy, exercise]);

  // Stats
  const withActual = rows.filter(r => r.actualDate);
  const onTime = withActual.filter(r => Math.abs(r.variance) <= 2).length;
  const late = withActual.filter(r => r.variance > 2).length;
  const early = withActual.filter(r => r.variance < -2).length;
  const avgDeviation = withActual.length > 0
    ? Math.round(withActual.reduce((s, r) => s + (r.variance || 0), 0) / withActual.length)
    : 0;
  const maxLate = withActual.length > 0
    ? Math.max(...withActual.map(r => r.variance || 0))
    : 0;
  const notTriggered = rows.filter(r => !r.actualDate).length;
  const onTimePct = withActual.length > 0 ? Math.round((onTime / withActual.length) * 100) : 0;

  // Distribution for sparkline
  const buckets = useMemo(() => {
    const b = { earlyBig: 0, earlySm: 0, onTime: 0, lateSm: 0, lateBig: 0 };
    withActual.forEach(r => {
      const v = r.variance || 0;
      if (v < -10) b.earlyBig++;
      else if (v < -2) b.earlySm++;
      else if (v <= 2) b.onTime++;
      else if (v <= 15) b.lateSm++;
      else b.lateBig++;
    });
    return b;
  }, [withActual]);
  const maxBucket = Math.max(1, ...Object.values(buckets));

  if (rows.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-lg">
        <GitCompareArrows className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <p className="text-sm font-medium">No injects to compare</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="compare-view">
      {/* ── Metric Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <div className="rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 p-3">
          <p className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 font-semibold">On Time</p>
          <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400">{onTime}</p>
          <p className="text-[10px] text-emerald-600/60 dark:text-emerald-400/60">{onTimePct}% accuracy</p>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20 p-3">
          <p className="text-[10px] uppercase tracking-wider text-red-600 dark:text-red-400 font-semibold">Late</p>
          <p className="text-2xl font-black text-red-600 dark:text-red-400">{late}</p>
          <p className="text-[10px] text-red-600/60 dark:text-red-400/60">Worst: {formatOffset(maxLate)}</p>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 p-3">
          <p className="text-[10px] uppercase tracking-wider text-blue-600 dark:text-blue-400 font-semibold">Early</p>
          <p className="text-2xl font-black text-blue-600 dark:text-blue-400">{early}</p>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 p-3">
          <p className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-semibold">Avg Deviation</p>
          <p className="text-2xl font-black text-amber-600 dark:text-amber-400">{formatOffset(avgDeviation)}</p>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-violet-500/10 to-violet-600/5 border border-violet-500/20 p-3">
          <p className="text-[10px] uppercase tracking-wider text-violet-600 dark:text-violet-400 font-semibold">Triggered</p>
          <p className="text-2xl font-black text-violet-600 dark:text-violet-400">{withActual.length}</p>
          <p className="text-[10px] text-violet-600/60 dark:text-violet-400/60">of {rows.length}</p>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-slate-500/10 to-slate-600/5 border border-slate-500/20 p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Pending</p>
          <p className="text-2xl font-black text-muted-foreground">{notTriggered}</p>
          <p className="text-[10px] text-muted-foreground/60">not triggered</p>
        </div>
      </div>

      {/* ── Deviation Distribution ── */}
      <div className="rounded-xl border p-4 bg-card">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
          <BarChart3 className="w-3.5 h-3.5" /> Deviation Distribution
        </h4>
        <div className="flex items-end gap-1 h-16">
          {[
            { key: 'earlyBig', label: '>10m early', color: 'bg-blue-500', val: buckets.earlyBig },
            { key: 'earlySm', label: '2-10m early', color: 'bg-blue-400', val: buckets.earlySm },
            { key: 'onTime', label: 'On time', color: 'bg-emerald-500', val: buckets.onTime },
            { key: 'lateSm', label: '2-15m late', color: 'bg-amber-500', val: buckets.lateSm },
            { key: 'lateBig', label: '>15m late', color: 'bg-red-500', val: buckets.lateBig },
          ].map(b => (
            <div key={b.key} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-[10px] font-bold text-foreground">{b.val}</span>
              <div
                className={`w-full rounded-t-md ${b.color} transition-all duration-500`}
                style={{ height: `${Math.max(4, (b.val / maxBucket) * 48)}px` }}
              />
              <span className="text-[8px] text-muted-foreground text-center leading-tight">{b.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Sort controls ── */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase text-muted-foreground font-semibold">Sort by:</span>
        {[
          { key: 'planned', label: 'Planned Time' },
          { key: 'deviation', label: 'Biggest Deviation' },
          { key: 'number', label: 'Inject #' },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setSortBy(s.key)}
            className={`text-[10px] px-2.5 py-1 rounded-full transition-colors ${sortBy === s.key ? 'bg-primary text-primary-foreground font-medium' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
            data-testid={`compare-sort-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* ── Comparison Table ── */}
      <ScrollArea className="h-[460px]">
        <div className="space-y-1.5 pr-2">
          {rows.map((row, idx) => {
            const isTriggered = !!row.actualDate;
            const v = row.variance;
            const absV = Math.abs(v || 0);

            // Deviation bar width (max 80px)
            const barWidth = isTriggered ? Math.min(80, Math.max(8, absV * 1.5)) : 0;
            let barColor = 'bg-emerald-500';
            if (v !== null) {
              if (v > 15) barColor = 'bg-red-500';
              else if (v > 2) barColor = 'bg-amber-500';
              else if (v < -2) barColor = 'bg-blue-500';
            }

            return (
              <div
                key={row.inject_id || idx}
                className={`rounded-lg border transition-all hover:shadow-md ${
                  !isTriggered ? 'opacity-50 border-dashed' :
                  absV <= 2 ? 'border-emerald-500/30 bg-emerald-500/[0.03]' :
                  v > 15 ? 'border-red-500/30 bg-red-500/[0.03]' :
                  v > 2 ? 'border-amber-500/30 bg-amber-500/[0.03]' :
                  v < -2 ? 'border-blue-500/30 bg-blue-500/[0.03]' :
                  'border-border'
                }`}
                data-testid={`compare-row-${row.inject_id}`}
              >
                <div className="p-3 grid grid-cols-[40px_1fr] gap-2">
                  {/* Left: Inject Number */}
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <span className="text-xs font-mono font-bold text-muted-foreground">#{row.inject_number || '?'}</span>
                  </div>

                  {/* Right: Content */}
                  <div className="min-w-0 space-y-1.5">
                    {/* Title Row */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium truncate">{row.title?.split('\n')[0] || 'Unnamed'}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold shrink-0 ${PRIORITY_BADGE[row.priority] || ''}`}>{row.priority}</span>
                      {row.phase !== 'Ungrouped' && (
                        <span className="text-[10px] text-muted-foreground truncate shrink-0">{row.phase}</span>
                      )}
                    </div>

                    {/* Time Comparison Row */}
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* Planned */}
                      <div className="flex items-center gap-1">
                        <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold w-12">Plan:</span>
                        <span className="text-xs font-mono font-medium bg-muted px-1.5 py-0.5 rounded">
                          {row.plannedOffset !== null ? formatOffset(row.plannedOffset) : '\u2014'}
                        </span>
                      </div>

                      {/* Arrow + Deviation Bar */}
                      <div className="flex items-center gap-1 min-w-[120px]">
                        {isTriggered ? (
                          <>
                            <div className={`h-1 rounded-full flex-1 min-w-[40px] max-w-[80px] ${barColor}`} style={{ width: `${barWidth}px` }} />
                            <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
                          </>
                        ) : (
                          <span className="text-[10px] text-muted-foreground italic">...</span>
                        )}
                      </div>

                      {/* Actual */}
                      <div className="flex items-center gap-1">
                        <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold w-12">Real:</span>
                        <span className={`text-xs font-mono font-medium px-1.5 py-0.5 rounded ${isTriggered ? 'bg-muted' : 'text-muted-foreground italic'}`}>
                          {isTriggered && row.actualOffset !== null ? formatOffset(row.actualOffset) : isTriggered ? formatTimeForTz(row.actualDate, timezone) : 'Pending'}
                        </span>
                      </div>

                      {/* Variance Badge */}
                      {isTriggered ? (
                        <VarianceBadge minutes={v} />
                      ) : (
                        <span className="text-[10px] text-muted-foreground italic flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Waiting
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

export default function ExerciseTimeline({
  exercise,
  injects = [],
  timelineEvents = [],
  timezone = 'UTC',
  showOnlyActual = false,
}) {
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showDeviation, setShowDeviation] = useState(false);
  const [view, setView] = useState('list'); // list | gantt | phases | compare
  const canCompare = !showOnlyActual; // Only admins/simcell can see Compare

  const timelineInjects = useMemo(() => {
    return injects
      .filter(inj => {
        if (priorityFilter !== 'All' && inj.priority !== priorityFilter.toLowerCase()) return false;
        if (statusFilter !== 'All' && inj.status !== statusFilter.toLowerCase()) return false;
        return true;
      })
      .sort((a, b) => {
        const aTime = parseInjectTime(getPlannedTime(a));
        const bTime = parseInjectTime(getPlannedTime(b));
        if (aTime && bTime) return aTime - bTime;
        if (aTime) return -1;
        if (bTime) return 1;
        return (parseInt(a.inject_number) || 0) - (parseInt(b.inject_number) || 0);
      });
  }, [injects, priorityFilter, statusFilter]);

  // Stats
  const totalInjects = injects.length;
  const triggered = injects.filter(i => i.triggered_at).length;
  const completed = injects.filter(i => i.status === 'completed').length;
  const behindSchedule = injects.filter(i => {
    const planned = parseInjectTime(getPlannedTime(i));
    const actual = i.triggered_at ? new Date(i.triggered_at) : null;
    if (!planned || !actual) return false;
    return getVariance(planned, actual) > 5;
  }).length;

  const priorities = ['All', 'High', 'Medium', 'Low'];
  const statuses = ['All', 'Draft', 'Ongoing', 'Completed'];

  // Participant view
  if (showOnlyActual) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" /> Live Events</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ActivityFeed events={timelineEvents} timezone={timezone} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="w-4 h-4" />
            Schedule vs Live
          </CardTitle>
          <div className="flex items-center gap-2">
            <ElapsedTimer exercise={exercise} />
            <ExportTimelineButton injects={injects} exercise={exercise} timezone={timezone} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Progress bar */}
        {totalInjects > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-muted/50">
            <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1.5">
              <span>Exercise Progress</span>
              <span>{Math.round((completed / totalInjects) * 100)}% complete</span>
            </div>
            <div className="h-3 rounded-full bg-muted overflow-hidden flex">
              {completed > 0 && <div className="bg-green-500 transition-all duration-500 rounded-l-full" style={{ width: `${(completed / totalInjects) * 100}%` }} />}
              {(triggered - completed) > 0 && <div className="bg-yellow-500 transition-all duration-500" style={{ width: `${((triggered - completed) / totalInjects) * 100}%` }} />}
              {(totalInjects - triggered) > 0 && <div className="bg-muted-foreground/20 transition-all duration-500 rounded-r-full" style={{ width: `${((totalInjects - triggered) / totalInjects) * 100}%` }} />}
            </div>
            <div className="flex items-center gap-4 mt-1.5 text-[10px]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Completed ({completed})</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" /> In Progress ({triggered - completed})</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-muted-foreground/20" /> Pending ({totalInjects - triggered})</span>
            </div>
          </div>
        )}

        {/* Summary stats */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="rounded-lg bg-muted/50 p-2 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Total</p>
            <p className="text-lg font-bold">{totalInjects}</p>
          </div>
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-2 text-center">
            <p className="text-[10px] text-blue-600 dark:text-blue-400 uppercase">Triggered</p>
            <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{triggered}</p>
          </div>
          <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-2 text-center">
            <p className="text-[10px] text-green-600 dark:text-green-400 uppercase">Completed</p>
            <p className="text-lg font-bold text-green-600 dark:text-green-400">{completed}</p>
          </div>
          <div className="rounded-lg bg-orange-50 dark:bg-orange-900/20 p-2 text-center">
            <p className="text-[10px] text-orange-600 dark:text-orange-400 uppercase">Behind</p>
            <p className="text-lg font-bold text-orange-600 dark:text-orange-400">{behindSchedule}</p>
          </div>
        </div>

        {/* View Switcher + Filters */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex gap-3 flex-wrap items-center">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase text-muted-foreground font-medium">Priority:</span>
              {priorities.map(p => (
                <button key={p} onClick={() => setPriorityFilter(p)}
                  className={`text-xs px-2 py-0.5 rounded-full transition-colors ${priorityFilter === p ? 'bg-primary text-primary-foreground font-medium' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                  data-testid={`priority-filter-${p.toLowerCase()}`}>{p}</button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase text-muted-foreground font-medium">Status:</span>
              {statuses.map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`text-xs px-2 py-0.5 rounded-full transition-colors ${statusFilter === s ? 'bg-primary text-primary-foreground font-medium' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                  data-testid={`status-filter-${s.toLowerCase()}`}>{s}</button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Deviation heatmap toggle */}
            <button
              onClick={() => setShowDeviation(d => !d)}
              className={`text-[10px] px-2.5 py-1 rounded-full transition-colors flex items-center gap-1 ${showDeviation ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
              data-testid="deviation-toggle"
              title="Toggle deviation heatmap"
            >
              <BarChart3 className="w-3 h-3" /> Heatmap
            </button>

            {/* View switcher */}
            <div className="flex items-center border rounded-lg overflow-hidden">
              {[
                { key: 'list', icon: List, label: 'List' },
                { key: 'gantt', icon: BarChart3, label: 'Gantt' },
                { key: 'phases', icon: Layers, label: 'Phases' },
                ...(canCompare ? [{ key: 'compare', icon: GitCompareArrows, label: 'Compare' }] : []),
              ].map(v => (
                <button
                  key={v.key}
                  onClick={() => setView(v.key)}
                  className={`flex items-center gap-1 px-2.5 py-1 text-[10px] transition-colors ${view === v.key ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'}`}
                  data-testid={`view-${v.key}`}
                >
                  <v.icon className="w-3 h-3" /> {v.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* View Content */}
        {view === 'gantt' && (
          <GanttChart injects={timelineInjects} timezone={timezone} exercise={exercise} />
        )}

        {view === 'phases' && (
          <PhaseGroupView injects={timelineInjects} timezone={timezone} showDeviation={showDeviation} />
        )}

        {view === 'compare' && (
          <CompareView injects={timelineInjects} timezone={timezone} exercise={exercise} />
        )}

        {view === 'list' && (
          <>
            {timelineInjects.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-lg">
                <CalendarClock className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">No injects match the current filters</p>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="pr-2 space-y-0">
                  {timelineInjects.map((inject, idx) => (
                    <InjectTimelineCard key={inject.inject_id || idx} inject={inject} timezone={timezone} showDeviation={showDeviation} />
                  ))}
                </div>
              </ScrollArea>
            )}
          </>
        )}

        {/* Activity Feed with Filters */}
        {timelineEvents.length > 0 && (
          <ActivityFeed events={timelineEvents} timezone={timezone} />
        )}
      </CardContent>
    </Card>
  );
}
